import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { 
  faArrowLeft, 
  faExclamationTriangle, 
  faSpider, 
  faLink, 
  faGlobe, 
  faDatabase, 
  faListAlt, 
  faSearch, 
  faStop,
  faCog,
  faRocket,
  faCheckCircle,
  faExclamationCircle,
  faSpinner,
  faTimesCircle,
  faPalette,
  faWandMagicSparkles,
  faClock,
  faMinus
} from '@fortawesome/free-solid-svg-icons';
import { getCrawlStatus, createCrawlStatusStream } from '../services/api';
import { getActiveCrawlJobs, updateCrawlJob } from '../services/crawlStatusService';
import { toast } from 'react-toastify';
import { 
  getStageInfo, 
  getProgressSteps, 
  isCompleted, 
  isFailed,
  calculateProgress,
  type ProgressStatus 
} from '../utils/statusMapper';

const ensureGlobalProgressStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('global-progress-style')) return;
  const style = document.createElement('style');
  style.id = 'global-progress-style';
  style.textContent = `
    @keyframes progress {
      0% { width: 0%; }
      100% { width: 100%; }
    }
    @keyframes progressSheen {
      0% { background-position: 0% 0%; }
      100% { background-position: -200% 0%; }
    }
    .progress-indeterminate {
      background-size: 200% 100%;
      animation: progressSheen 1.25s linear infinite;
    }
  `;
  document.head.appendChild(style);
};

interface LocationState {
  fromRescrap?: boolean;
  pendingUrl?: string;
}

interface RecentPageSummary {
  url: string;
  title?: string;
  status_code?: number;
  error?: string | null;
}

type ChunkPageStatus = 'processing' | 'embedding' | 'embedded' | 'failed';

interface ChunkPage {
  url: string;
  status: ChunkPageStatus;
  lastUpdated: number;
}

interface FirecrawlStatus {
  id: string;
  client_id: string;
  status:
    | 'initializing'
    | 'requesting'
    | 'crawling_remote'
    | 'processing_results'
    | 'in_progress'
    | 'embedding'
    | 'completed'
    | 'failed'
    | 'no_crawl'
    | 'stopped'
    | 'aborted'
    | 'crawling'
    | 'extracting_branding'
    | 'generating_themes';
  url: string;
  max_pages?: number;
  pages_in_queue: number | string;
  current_url?: string;
  visited_urls?: string[];
  newly_visited_urls?: string[];
  latest_url?: string;
  visited_urls_total?: number;
  visited_urls_trimmed?: boolean;
  pending_urls?: string[];
  errors?: string[];
  progress_percentage?: number;
  progress?: number;
  progress_label?: string;
  embedding_status?: 'pending' | 'queued' | 'in_progress' | 'completed' | 'failed' | 'skipped';
  embedding_progress?: number;
  embedding_current?: number;
  embedding_total?: number;
  pages_scraped?: number;
  chunks_total?: number;
  chunks_completed?: number;
  current_chunk?: number | null;
  chunk_size?: number;
  chunk_index?: number | null;
  pages_failed?: number;
  recent_pages?: RecentPageSummary[];
  polling_mode?: boolean;
  invalid_urls?: string[];
  active_chunks?: number[];
  total_pages?: number | string;
  estimated_total_pages?: number;
  remaining_urls_estimate?: number | string;
  start_time?: string;
  end_time?: string;
  last_update?: string;
  message?: string;
  phase?: string;
  engine?: string;
  data_stored?: boolean;
  pdf_documents_found?: number;
  error?: string;
  branding_status?: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'failed';
  theme_variants_status?: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'failed';
  theme_variants_count?: number;
  branding_errors?: string[] | string;
  branding_details?: {
    primary_color?: string;
    secondary_color?: string;
    primary_font?: string;
  };
  pages_remaining?: number | string;
  pages_processed?: number;
}

const STREAM_VISITED_CACHE_LIMIT = 50;

const STAGE_GRADIENTS: Record<string, string> = {
  blue: 'from-brand-500 to-brand-500',
  purple: 'from-brand-500 to-brand-600',
  indigo: 'from-brand-500 to-brand-600',
  violet: 'from-brand-500 to-brand-600',
  green: 'from-brand-500 to-brand-600',
  gray: 'from-gray-400 to-gray-500',
  red: 'from-red-500 to-red-600',
  yellow: 'from-yellow-500 to-yellow-600'
};

const STAGE_ACCENT_COLORS: Record<string, string> = {
  blue: 'text-brand-500',
  purple: 'text-brand-500',
  indigo: 'text-brand-500',
  violet: 'text-brand-500',
  green: 'text-brand-500',
  gray: 'text-gray-500',
  red: 'text-red-500',
  yellow: 'text-yellow-500'
};

const clampProgressValue = (value?: number | null): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0;
  }
  return Math.min(100, Math.max(0, value));
};

const formatStatNumber = (value?: number | string | null): string => {
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  if (typeof value === 'string' && value.trim().length) {
    const numeric = Number(value.replace(/[^0-9.-]/g, ''));
    if (!Number.isNaN(numeric)) {
      return numeric.toLocaleString();
    }
    return value;
  }
  return '0';
};

const formatPrimaryStat = (value?: number | null, total?: number | null): string => {
  const display = typeof value === 'number' ? value.toLocaleString() : '0';
  if (typeof total === 'number' && total > 0) {
    return `${display} / ${total.toLocaleString()}`;
  }
  return display;
};

const humanizeEmbeddingState = (state?: string | null): string => {
  if (!state) return 'Not started';
  switch (state) {
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'in_progress':
      return 'Running';
    case 'queued':
      return 'Queued';
    case 'pending':
      return 'Pending';
    case 'skipped':
      return 'Skipped';
    default:
      return state.replace(/_/g, ' ');
  }
};

const formatProgressValue = (value: number | string): string => {
  if (typeof value === 'number') {
    return value.toString();
  }
  return value || '0';
};

const CrawlDetails: React.FC = () => {
  useEffect(() => {
    ensureGlobalProgressStyles();
  }, []);
  const params = useParams<{ clientId: string }>();
  const clientId = params.clientId;
  const navigate = useNavigate();
  const location = useLocation();
  const [crawlData, setCrawlData] = useState<FirecrawlStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Removed activeTab - tabs are no longer needed
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Removed: Stop & Abort button state (buttons removed per requirements)
  const [streamRetryCount, setStreamRetryCount] = useState(0);
  const eventSourceRef = useRef<EventSource | null>(null);
  const hasStreamBeenChecked = useRef<boolean>(false);
  const hasCompletedNotification = useRef<boolean>(false);
  const [chunkPages, setChunkPages] = useState<Record<number, ChunkPage[]>>({});
  const [chunkTargets, setChunkTargetsState] = useState<Record<number, number>>({});
  const chunkTargetsRef = useRef<Record<number, number>>({});
  const updateChunkTargets = useCallback((updater: (prev: Record<number, number>) => Record<number, number>) => {
    setChunkTargetsState(prev => {
      const next = updater(prev);
      chunkTargetsRef.current = next;
      return next;
    });
  }, []);
  const crawlDataRef = useRef<FirecrawlStatus | null>(null);
  const prevVisitedRef = useRef<string[]>([]);
  const prevCompletedRef = useRef<number>(0);
  const [latestUrl, setLatestUrl] = useState<string | null>(null);

  // Check if this is a redirect from a rescrap operation
  const locationState = location.state as LocationState | null;
  const [pendingUrl] = useState(locationState?.pendingUrl);
  const [isFromRescrap] = useState(!!locationState?.fromRescrap);

  const chunkInfo = useMemo(() => {
    if (!crawlData) return null;
    const {
      chunks_total,
      chunks_completed,
      current_chunk,
      active_chunks,
      status,
      phase
    } = crawlData;
    if (!chunks_total || chunks_total <= 0) {
      return null;
    }
    const total = chunks_total;
    const completed = chunks_completed ?? 0;
    const activeChunkList = Array.isArray(active_chunks)
      ? active_chunks.filter((chunk): chunk is number => typeof chunk === 'number' && chunk > 0).sort((a, b) => a - b)
      : [];
    const currentFromActive = activeChunkList.length ? activeChunkList[0] : null;
    const currentFromStatus =
      typeof current_chunk === 'number' && current_chunk > 0
        ? current_chunk
        : null;
    const current =
      currentFromActive ||
      currentFromStatus ||
      (completed < total ? completed + 1 : total);
    const isEmbeddingPhase =
      status === 'embedding' || phase === 'generating_embeddings';
    return {
      total,
      completed,
      current,
      activeChunks: activeChunkList,
      isEmbedding: isEmbeddingPhase
    };
  }, [crawlData]);

  const totalProcessedPages = useMemo(() => {
    if (!crawlData) return 0;
    const { pages_scraped, pages_processed, visited_urls_total, visited_urls } = crawlData;

    // Priority 1: Use pages_processed (actual stored pages)
    if (typeof pages_processed === 'number' && pages_processed >= 0) {
      return pages_processed;
    }

    // Priority 2: Fall back to pages_scraped (should equal pages_processed)
    if (typeof pages_scraped === 'number' && pages_scraped >= 0) {
      return pages_scraped;
    }

    // Priority 3: Fall back to visited_urls_total
    if (typeof visited_urls_total === 'number' && visited_urls_total >= 0) {
      return visited_urls_total;
    }

    // Priority 4: Count visited_urls array
    if (Array.isArray(visited_urls)) {
      return visited_urls.length;
    }

    return 0;
  }, [crawlData]);

  const pagesFailedCount = crawlData?.pages_failed ?? (crawlData?.errors?.length ?? 0);

  const pagesRemaining = useMemo(() => {
    if (!crawlData) return 0;

    const {
      pages_remaining,
      pages_in_queue,
      estimated_total_pages,
      total_pages,
      chunk_size,
      chunks_total,
      chunks_completed
    } = crawlData;

    const remainingDirect = typeof pages_remaining === 'number'
      ? pages_remaining
      : typeof pages_remaining === 'string'
        ? Number.parseInt(pages_remaining.replace(/[^0-9-]/g, ''), 10)
        : null;

    if (typeof remainingDirect === 'number' && !Number.isNaN(remainingDirect)) {
      return Math.max(remainingDirect, 0);
    }

    const totalFromMetadata =
      typeof total_pages === 'number' && total_pages >= 0
        ? total_pages
        : typeof estimated_total_pages === 'number' && estimated_total_pages >= 0
          ? estimated_total_pages
          : null;

    if (totalFromMetadata !== null) {
      return Math.max(totalFromMetadata - totalProcessedPages - pagesFailedCount, 0);
    }

    const numericQueue =
      typeof pages_in_queue === 'number'
        ? pages_in_queue
        : typeof pages_in_queue === 'string'
          ? Number.parseInt(pages_in_queue.replace(/[^0-9]/g, ''), 10)
          : Number.NaN;

    if (!Number.isNaN(numericQueue)) {
      return Math.max(numericQueue - pagesFailedCount, 0);
    }

    const inferredTotal =
      typeof chunk_size === 'number' &&
      typeof chunks_total === 'number' &&
      typeof chunks_completed === 'number'
        ? Math.max(chunk_size, 0) * Math.max(chunks_total - chunks_completed, 0)
        : 0;

    return Math.max(inferredTotal, 0);
  }, [crawlData, pagesFailedCount, totalProcessedPages]);

  const chunkProgress = useMemo(() => {
    if (!crawlData) {
      return {
        total: 0,
        completed: 0,
        current: null as number | null,
        activeChunks: [] as number[],
        expectedUrls: 0,
        processedUrls: 0,
        embeddingUrls: 0,
        failedUrls: 0,
        scrapingUrls: 0,
        isEmbedding: false,
        pages: [] as ChunkPage[],
        percent: 0
      };
    }

    if (!chunkInfo || !chunkInfo.total) {
      return {
        total: crawlData.chunks_total ?? 0,
        completed: crawlData.chunks_completed ?? 0,
        current: typeof crawlData.current_chunk === 'number' ? crawlData.current_chunk : null,
        activeChunks: Array.isArray(crawlData.active_chunks)
          ? crawlData.active_chunks.filter((chunk): chunk is number => typeof chunk === 'number' && chunk > 0)
          : [],
        expectedUrls: crawlData.chunk_size ?? 0,
        processedUrls: 0,
        embeddingUrls: 0,
        failedUrls: 0,
        scrapingUrls: 0,
        isEmbedding: crawlData.status === 'embedding',
        pages: [] as ChunkPage[],
        percent: Math.min(crawlData.progress_percentage ?? 0, 100),
        target: crawlData.chunk_size ?? 0
      };
    }

    const { total, completed, current, isEmbedding, activeChunks } = chunkInfo;
    const currentChunkId = current ?? (activeChunks && activeChunks.length ? activeChunks[0] : null);
    const pagesForChunk = currentChunkId ? [...(chunkPages[currentChunkId] ?? [])] : [];

    const processedUrls = pagesForChunk.filter(page => page.status === 'embedded').length;
    const embeddingUrls = pagesForChunk.filter(page => page.status === 'embedding').length;
    const failedUrls = pagesForChunk.filter(page => page.status === 'failed').length;
    const scrapingUrls = pagesForChunk.filter(page => page.status === 'processing').length;

    const targetFromState = currentChunkId ? chunkTargets[currentChunkId] : undefined;
    let targetForChunk =
      typeof targetFromState === 'number' && targetFromState > 0 ? targetFromState : 0;

    if (!targetForChunk && typeof crawlData.chunk_size === 'number' && crawlData.chunk_size > 0) {
      targetForChunk = crawlData.chunk_size;
      if (currentChunkId === total) {
        const totalPagesValue = typeof crawlData.total_pages === 'number'
          ? crawlData.total_pages
          : typeof crawlData.estimated_total_pages === 'number'
            ? crawlData.estimated_total_pages
            : undefined;
        if (totalPagesValue && crawlData.chunk_size > 0) {
          const remainder = totalPagesValue - crawlData.chunk_size * (total - 1);
          if (remainder > 0 && remainder < crawlData.chunk_size) {
            targetForChunk = remainder;
          }
        }
      }
    }

    if (!targetForChunk && pagesForChunk.length > 0) {
      targetForChunk = pagesForChunk.length;
    }

    const expectedUrls = targetForChunk > 0 ? targetForChunk : pagesForChunk.length;

    const denominator = expectedUrls > 0 ? expectedUrls : (pagesForChunk.length || 1);
    const currentChunkFraction = Math.min(
      (processedUrls + embeddingUrls + failedUrls + scrapingUrls) / denominator,
      1
    );

    const percent = total > 0
      ? Math.min(((completed + currentChunkFraction) / total) * 100, 100)
      : 0;

    return {
      total,
      completed,
      current: currentChunkId,
      activeChunks,
      expectedUrls,
      processedUrls,
      embeddingUrls,
      failedUrls,
      scrapingUrls,
      isEmbedding: isEmbedding || crawlData.status === 'embedding',
      pages: pagesForChunk,
      percent,
      target: targetForChunk
    };
  }, [crawlData, chunkInfo, chunkPages, chunkTargets]);

  const currentChunkPages = useMemo(() => {
    return [...chunkProgress.pages].sort((a, b) => b.lastUpdated - a.lastUpdated);
  }, [chunkProgress.pages]);

  const displayLatestUrl = useMemo(() => {
    if (latestUrl) return latestUrl;
    if (currentChunkPages.length) return currentChunkPages[0].url;
    if (crawlData?.current_url) return crawlData.current_url;
    if (crawlData?.recent_pages && crawlData.recent_pages.length) {
      return crawlData.recent_pages[0].url;
    }
    return null;
  }, [latestUrl, currentChunkPages, crawlData?.current_url, crawlData?.recent_pages]);

  useEffect(() => {
    if (!chunkInfo) {
      setChunkPages({});
      updateChunkTargets(() => ({}));
      return;
    }

    setChunkPages(prevPages => {
      const updated = { ...prevPages };
      for (let chunk = 1; chunk <= chunkInfo.total; chunk++) {
        if (!updated[chunk]) {
          updated[chunk] = [];
        }
      }
      return updated;
    });

    updateChunkTargets(prevTargets => {
      const updatedTargets = { ...prevTargets };
      const defaultSize =
        typeof crawlData?.chunk_size === 'number' && crawlData.chunk_size > 0
          ? crawlData.chunk_size
          : undefined;
      for (let chunk = 1; chunk <= chunkInfo.total; chunk++) {
        if (updatedTargets[chunk] === undefined && defaultSize) {
          updatedTargets[chunk] = defaultSize;
        }
      }
      return updatedTargets;
    });
  }, [chunkInfo, crawlData?.chunk_size, updateChunkTargets]);

  // Clear old data when navigating from a new crawl
  useEffect(() => {
    if (isFromRescrap) {
      console.log('[CRAWL-DETAILS] New crawl initiated, clearing old data');
      setCrawlData(null);
      setLoading(true);
      hasCompletedNotification.current = false;
      setChunkPages({});
      updateChunkTargets(() => ({}));
      prevVisitedRef.current = [];
      prevCompletedRef.current = 0;
      crawlDataRef.current = null;
      setLatestUrl(null);
    }
  }, [isFromRescrap]);

  const parseNumericValue = useCallback((value: unknown): number => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const numericMatch = value.match(/-?\d+/);
      if (numericMatch) {
        const parsed = parseInt(numericMatch[0], 10);
        return Number.isNaN(parsed) ? 0 : parsed;
      }
    }
    return 0;
  }, []);

  const attemptAutoRedirect = useCallback((statusData: FirecrawlStatus | null | undefined) => {
    if (!statusData || statusData.status !== 'completed') {
      console.log('[CRAWL-DETAILS] Auto-redirect skipped: status is not completed', statusData?.status);
      return;
    }

    const chunkTotal =
      parseNumericValue(statusData.chunks_total ?? (statusData as any)?.chunksTotal);
    const chunkCompleted =
      parseNumericValue(statusData.chunks_completed ?? (statusData as any)?.chunksCompleted);
    const chunksRemaining = chunkTotal > 0 && chunkCompleted < chunkTotal;

    const brandingStatus = statusData.branding_status;
    const themeStatus = statusData.theme_variants_status;

    const brandingDone =
      !brandingStatus || ['completed', 'skipped', 'failed'].includes(brandingStatus);
    const themeDone =
      !themeStatus || ['completed', 'skipped', 'failed'].includes(themeStatus);
    const postProcessingPending = !brandingDone || !themeDone;

    console.log('[CRAWL-DETAILS] Redirect check:', {
      status: statusData.status,
      chunkTotal,
      chunkCompleted,
      chunksRemaining,
      brandingStatus,
      themeStatus,
      brandingDone,
      themeDone,
      postProcessingPending,
      hasCompletedNotification: hasCompletedNotification.current
    });

    if (!chunksRemaining && !postProcessingPending && !hasCompletedNotification.current) {
      hasCompletedNotification.current = true;
      console.log('[CRAWL-DETAILS] Crawl completed – redirecting to knowledge base shortly');

      setTimeout(() => {
        const brandingFailed = brandingStatus === 'failed';
        const themeFailed = themeStatus === 'failed';
        if (brandingFailed || themeFailed) {
          toast.warn(
            'Crawl completed. Branding post-processing reported warnings—review details in the Knowledge Base.'
          );
        } else {
          toast.success('Crawl and knowledge base creation completed successfully!');
        }
        navigate('/knowledge-base');
      }, 2000);
    }
  }, [navigate, parseNumericValue]);

  const fetchHistoricalData = useCallback(async () => {
    if (!clientId) return;
    
    try {
      console.log(`[CRAWL-DETAILS] Fetching historical data for ${clientId}`);
      setLoading(true);
      const response = await getCrawlStatus(clientId);

      const statusPayload = response?.success ? response.data : response;

      if (statusPayload && statusPayload.status && statusPayload.status !== 'no_crawl') {
        const historyLatest =
          (Array.isArray(statusPayload.visited_urls) && statusPayload.visited_urls.length
            ? statusPayload.visited_urls[statusPayload.visited_urls.length - 1]
            : null) ||
          statusPayload.current_url ||
          null;

        console.log(`[CRAWL-DETAILS] Historical data found:`, statusPayload);
        const normalizedData: FirecrawlStatus = {
          ...statusPayload,
          client_id: clientId
        };

        setCrawlData(normalizedData);
        crawlDataRef.current = normalizedData;
        attemptAutoRedirect(normalizedData);

        if (historyLatest) {
          setLatestUrl(historyLatest);
        }
      } else {
        console.log(`[CRAWL-DETAILS] No historical crawl data found`);
        const placeholder: FirecrawlStatus = {
          id: `no-crawl-${clientId}`,
          client_id: clientId,
          status: 'no_crawl',
          url: '',
          pages_scraped: 0,
          pages_processed: 0,
          pages_in_queue: 0,
          message: 'No crawl history found for this client'
        };
        setCrawlData(placeholder);
        crawlDataRef.current = placeholder;
        setLatestUrl(null);
      }
    } catch (error) {
      console.error(`[CRAWL-DETAILS] Error fetching historical data:`, error);
      setError('Failed to load crawl data');
    } finally {
      setLoading(false);
    }
  }, [attemptAutoRedirect, clientId]);
  
  // Enhanced stream handling with better error recovery
  useEffect(() => {
    if (!clientId) return;
    
    if (!hasStreamBeenChecked.current) {
      console.log(`[CRAWL-DETAILS] Checking for active stream for ${clientId}`);
      
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      
      // Small delay to ensure Redis status write completes
      // Reduced from 1000ms to 200ms since Redis persistence prevents missing updates
      const delay = isFromRescrap ? 200 : 0;
      
      setTimeout(() => {
        const eventSource = createCrawlStatusStream(
          clientId,
          (data) => {
            if (data && data.error && data.error.includes("No active crawl for this client")) {
              console.log(`[CRAWL-DETAILS] No active crawl detected`);
              hasStreamBeenChecked.current = true;
              
              if (isFromRescrap && streamRetryCount < 10) {
                console.log(`[CRAWL-DETAILS] Re-crawl initiated but stream not ready yet, waiting... (retry ${streamRetryCount + 1}/10)`);
                setLoading(false);
                
                // Don't reset state to placeholder if we already have real progress data
                const currentData = crawlDataRef.current;
                const hasRealProgress = currentData && (
                  (currentData.pages_processed && currentData.pages_processed > 0) ||
                  (currentData.pages_scraped && currentData.pages_scraped > 0) ||
                  (currentData.chunks_completed && currentData.chunks_completed > 0)
                );
                
                if (!hasRealProgress) {
                  // Only set placeholder if we don't have real data yet
                  setCrawlData({
                    id: `pending-${clientId}`,
                    client_id: clientId,
                    status: 'initializing',
                    url: pendingUrl || '',
                    pages_scraped: 0,
                    pages_processed: 0,
                    pages_in_queue: 'Please wait...',
                    engine: 'firecrawl',
                    message: 'Starting Firecrawl crawler, please wait...'
                  } as FirecrawlStatus);
                }
                
                const backoffDelay = Math.min(1000 * Math.pow(1.5, streamRetryCount), 10000);
                setTimeout(() => {
                  console.log(`[CRAWL-DETAILS] Retrying stream connection after ${backoffDelay}ms...`);
                  if (eventSourceRef.current) {
                    eventSourceRef.current.close();
                    eventSourceRef.current = null;
                  }
                  hasStreamBeenChecked.current = false;
                  setStreamRetryCount(prev => prev + 1);
                }, backoffDelay);
                return;
              }
              
              fetchHistoricalData();
              return;
            }
            
            if (data && !data.error && data.status !== 'no_crawl') {
              console.log(`[CRAWL-DETAILS] Received valid stream data:`, data);
              let lastVisitedInUpdate: string | null = null;
              setCrawlData(prev => {
                const prevVisited: string[] = prevVisitedRef.current ?? prev?.visited_urls ?? [];
                const snapshotFromPayload: string[] = Array.isArray(data.visited_urls)
                  ? data.visited_urls
                  : prev?.visited_urls ?? [];
                const deltaFromPayload: string[] = Array.isArray(data.newly_visited_urls)
                  ? data.newly_visited_urls
                  : [];
                const isInitialSnapshot =
                  prevVisited.length === 0 && deltaFromPayload.length === 0 && snapshotFromPayload.length > 0;
                const newVisitedUrls: string[] = deltaFromPayload.length
                  ? deltaFromPayload.filter((url: string) => !prevVisited.includes(url))
                  : isInitialSnapshot
                    ? snapshotFromPayload
                    : snapshotFromPayload.filter((url: string) => !prevVisited.includes(url));
                if (newVisitedUrls.length) {
                  lastVisitedInUpdate = newVisitedUrls[newVisitedUrls.length - 1];
                } else if (typeof data.latest_url === 'string') {
                  lastVisitedInUpdate = data.latest_url;
                }

                const mergedVisited: string[] = [...prevVisited];
                const appendUrl = (url: string) => {
                  if (!mergedVisited.includes(url)) {
                    mergedVisited.push(url);
                  }
                };
                if (deltaFromPayload.length) {
                  deltaFromPayload.forEach(appendUrl);
                } else if (snapshotFromPayload.length) {
                  snapshotFromPayload.forEach(appendUrl);
                }
                const trimmedVisited =
                  mergedVisited.length > STREAM_VISITED_CACHE_LIMIT
                    ? mergedVisited.slice(-STREAM_VISITED_CACHE_LIMIT)
                    : mergedVisited;

                const prevCompleted = prev?.chunks_completed ?? prevCompletedRef.current ?? 0;
                const nextCompleted = data.chunks_completed ?? prevCompleted;
                const totalChunks = data.chunks_total ?? prev?.chunks_total ?? 0;

                const rawCurrentChunk = typeof data.current_chunk === 'number' && data.current_chunk > 0
                  ? data.current_chunk
                  : (nextCompleted < totalChunks && totalChunks > 0
                      ? nextCompleted + 1
                      : totalChunks);

                const plannedCount = typeof data.chunk_urls_planned === 'number' && data.chunk_urls_planned > 0
                  ? data.chunk_urls_planned
                  : null;
                const payloadChunkIndex = typeof data.chunk_index === 'number' && data.chunk_index > 0
                  ? data.chunk_index
                  : null;
                const planningChunk = (payloadChunkIndex ?? rawCurrentChunk) && (payloadChunkIndex ?? rawCurrentChunk) > 0
                  ? (payloadChunkIndex ?? rawCurrentChunk)
                  : null;

                if (plannedCount && planningChunk) {
                  updateChunkTargets(prevTargets => {
                    if (prevTargets[planningChunk] === plannedCount) {
                      return prevTargets;
                    }
                    return {
                      ...prevTargets,
                      [planningChunk]: plannedCount
                    };
                  });
                }

                const combinedErrors = Array.isArray(data.errors)
                  ? data.errors
                  : prev?.errors ?? [];
                const invalidUrls = Array.isArray(data.invalid_urls) ? data.invalid_urls : [];

                setChunkPages(prevPages => {
                  const updated: Record<number, ChunkPage[]> = {};
                  Object.entries(prevPages).forEach(([chunk, pages]) => {
                    updated[Number(chunk)] = pages.map(p => ({ ...p }));
                  });

                  const targetChunk = planningChunk;

                  if (newVisitedUrls.length && targetChunk) {
                    const list = updated[targetChunk] ? [...updated[targetChunk]] : [];
                    const entriesByUrl = new Map(list.map(page => [page.url, page]));
                    newVisitedUrls.forEach((url: string) => {
                      if (!entriesByUrl.has(url)) {
                        const pageEntry: ChunkPage = { url, status: 'processing', lastUpdated: Date.now() };
                        list.push(pageEntry);
                        entriesByUrl.set(url, pageEntry);
                      }
                    });
                    const targetFromState = chunkTargetsRef.current[targetChunk];
                    const capacity =
                      (plannedCount && plannedCount > 0 ? plannedCount : undefined) ??
                      (typeof targetFromState === 'number' && targetFromState > 0 ? targetFromState : undefined) ??
                      (typeof data.chunk_size === 'number' && data.chunk_size > 0 ? data.chunk_size : undefined);
                    if (capacity && capacity > 0 && list.length > capacity) {
                      const overflow = list.length - capacity;
                      list.splice(0, overflow);
                    }
                    updated[targetChunk] = list;
                  }

                  const failedUrls: string[] = [];
                  combinedErrors.forEach((err: string) => {
                    const parts = err.split(':');
                    if (parts.length > 1) {
                      const urlCandidate = parts[0].trim();
                      if (urlCandidate.startsWith('http')) {
                        failedUrls.push(urlCandidate);
                      }
                    }
                  });
                  invalidUrls.forEach((url: string) => {
                    if (url && !failedUrls.includes(url)) failedUrls.push(url);
                  });

                  if (failedUrls.length) {
                    Object.keys(updated).forEach(chunkKey => {
                      const numKey = Number(chunkKey);
                      updated[numKey] = updated[numKey]?.map(page =>
                        failedUrls.includes(page.url)
                          ? { ...page, status: 'failed', lastUpdated: Date.now() }
                          : page
                      ) || [];
                    });
                  }

                  if (nextCompleted > prevCompleted) {
                    for (let chunk = 1; chunk <= nextCompleted; chunk++) {
                      if (updated[chunk]) {
                        updated[chunk] = updated[chunk].map(page =>
                          page.status === 'failed'
                            ? page
                            : { ...page, status: 'embedded', lastUpdated: Date.now() }
                        );
                      }
                    }
                  } else if (data.status === 'embedding' && rawCurrentChunk > 0 && updated[rawCurrentChunk]) {
                    updated[rawCurrentChunk] = updated[rawCurrentChunk].map(page =>
                      page.status === 'failed'
                        ? page
                        : { ...page, status: 'embedding', lastUpdated: Date.now() }
                    );
                  }

                  return updated;
                });

                prevVisitedRef.current = trimmedVisited;
                prevCompletedRef.current = nextCompleted;

                // Prefer fresh backend totals for pages_scraped but avoid regressing within the same crawl session
                const hasValidPagesScraped = (value: unknown): value is number =>
                  typeof value === 'number' && !Number.isNaN(value);
                const sameCrawlSession =
                  prev?.start_time && data.start_time
                    ? prev.start_time === data.start_time
                    : Boolean(prev?.start_time) === Boolean(data.start_time);
                const prevPagesScraped = hasValidPagesScraped(prev?.pages_scraped)
                  ? (prev?.pages_scraped as number)
                  : undefined;
                const resolvedPagesScraped = (() => {
                  if (hasValidPagesScraped(data.pages_scraped)) {
                    if (
                      sameCrawlSession &&
                      prevPagesScraped !== undefined &&
                      data.pages_scraped < prevPagesScraped
                    ) {
                      return prevPagesScraped;
                    }
                    return data.pages_scraped;
                  }
                  if (sameCrawlSession && prevPagesScraped !== undefined) {
                    return prevPagesScraped;
                  }
                  return undefined;
                })();
                // Preserve maximum embedding_total (defense in depth - backend should send cumulative)
                const preservedEmbeddingTotal = Math.max(
                  typeof data.embedding_total === 'number' ? data.embedding_total : 0,
                  typeof prev?.embedding_total === 'number' ? prev.embedding_total : 0
                );
                // Build complete object in one pass to ensure React detects changes
                // This ensures all values are set in the initial object creation, not mutated afterwards
                const updatedData: FirecrawlStatus = {
                  // Spread prev first (handle null case)
                  ...(prev || {}),
                  // Then spread data to override
                  ...data,
                  // Explicitly set all critical fields to ensure React detects changes
                  visited_urls: trimmedVisited,
                  latest_url: lastVisitedInUpdate ?? prev?.latest_url ?? data.latest_url,
                  errors: combinedErrors,
                  client_id: clientId,
                  // Always use SSE values if present (including 0), otherwise keep prev
                  chunks_completed: typeof data.chunks_completed === 'number' ? data.chunks_completed : (prev?.chunks_completed ?? 0),
                  chunks_total: typeof data.chunks_total === 'number' ? data.chunks_total : (prev?.chunks_total ?? 0),
                  pages_scraped: resolvedPagesScraped !== undefined ? resolvedPagesScraped : (prev?.pages_scraped ?? data.pages_scraped ?? 0),
                  pages_processed: typeof data.pages_processed === 'number' ? data.pages_processed : (prev?.pages_processed ?? 0),
                  progress: typeof data.progress === 'number' ? data.progress : (prev?.progress ?? data.progress_percentage ?? 0),
                  progress_label: typeof data.progress_label === 'string' ? data.progress_label : (prev?.progress_label ?? `${Math.round(typeof data.progress === 'number' ? data.progress : (prev?.progress ?? 0))}%`),
                  total_pages: typeof data.total_pages === 'number' || typeof data.total_pages === 'string' ? data.total_pages : (prev?.total_pages ?? data.estimated_total_pages ?? 0),
                  pages_remaining: typeof data.pages_remaining === 'number' || typeof data.pages_remaining === 'string' ? data.pages_remaining : (prev?.pages_remaining ?? data.pages_in_queue ?? 0),
                  pages_in_queue: typeof data.pages_in_queue === 'number' || typeof data.pages_in_queue === 'string' ? data.pages_in_queue : (prev?.pages_in_queue ?? 0),
                  visited_urls_total: typeof data.visited_urls_total === 'number' ? data.visited_urls_total : trimmedVisited.length,
                  newly_visited_urls: deltaFromPayload.length ? deltaFromPayload : [],
                  // Embedding fields
                  embedding_current: typeof data.embedding_current === 'number' ? data.embedding_current : prev?.embedding_current,
                  embedding_total: preservedEmbeddingTotal > 0 ? preservedEmbeddingTotal : prev?.embedding_total,
                  // Status and phase
                  status: data.status ?? prev?.status ?? 'initializing',
                  phase: data.phase ?? prev?.phase,
                  message: data.message ?? prev?.message,
                  // Other fields from data
                  start_time: data.start_time ?? prev?.start_time,
                  last_update: data.last_update ?? prev?.last_update,
                  pages_failed: typeof data.pages_failed === 'number' ? data.pages_failed : (prev?.pages_failed ?? 0),
                  active_chunks: Array.isArray(data.active_chunks) ? data.active_chunks : prev?.active_chunks,
                  current_chunk: typeof data.current_chunk === 'number' ? data.current_chunk : prev?.current_chunk,
                  chunk_index: typeof data.chunk_index === 'number' ? data.chunk_index : prev?.chunk_index,
                } as FirecrawlStatus;

                crawlDataRef.current = updatedData;
                
                // Debug logging to verify state updates
                console.log(`[CRAWL-DETAILS] State update:`, {
                  chunks_completed: updatedData.chunks_completed,
                  chunks_total: updatedData.chunks_total,
                  pages_scraped: updatedData.pages_scraped,
                  pages_processed: updatedData.pages_processed,
                  progress: updatedData.progress,
                  progress_label: updatedData.progress_label,
                  status: updatedData.status,
                  phase: updatedData.phase,
                  prev_chunks_completed: prev?.chunks_completed,
                  prev_pages_processed: prev?.pages_processed
                });
                
                // Force new object reference to ensure React detects the change
                // This prevents any potential issues with object reference equality
                return { ...updatedData };
              });
              if (lastVisitedInUpdate) {
                setLatestUrl(lastVisitedInUpdate);
              } else if (data.current_url) {
                setLatestUrl(prev => (prev === data.current_url ? prev : data.current_url));
              }
              setLoading(false);

              const activeCrawls = getActiveCrawlJobs();
              const activeJob = activeCrawls.find(job => job.clientId === clientId);
              if (activeJob) {
                updateCrawlJob(activeJob.id, {
                  ...data,
                  clientId
                });
              }

              if (data.status === 'completed' || data.status === 'aborted' || data.status === 'failed') {
                console.log(`[CRAWL-DETAILS] Crawl finished with status: ${data.status}`);
              }

              // Use the latest data instead of ref for redirect check
              attemptAutoRedirect(data);
            }
          }
        );
        
        eventSource.onerror = (error) => {
          console.log(`[CRAWL-DETAILS] Stream error or no active stream`, error);
          hasStreamBeenChecked.current = true;
          
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          
          if (isFromRescrap && streamRetryCount < 10) {
            const backoffDelay = Math.min(1000 * Math.pow(1.5, streamRetryCount), 10000);
            setTimeout(() => {
              console.log(`[CRAWL-DETAILS] Retrying after connection error...`);
              hasStreamBeenChecked.current = false;
              setStreamRetryCount(prev => prev + 1);
            }, backoffDelay);
          } else {
            fetchHistoricalData();
          }
        };
        
        eventSourceRef.current = eventSource;
      }, delay);
    }

    return () => {
      if (eventSourceRef.current) {
        console.log('[CRAWL-DETAILS] Cleaning up event source');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [attemptAutoRedirect, clientId, fetchHistoricalData, isFromRescrap, navigate, pendingUrl, streamRetryCount]);


  const getPostProcessMeta = (status?: string) => {
    const normalized = status ?? 'pending';
    switch (normalized) {
      case 'completed':
        return { label: 'Completed', icon: faCheckCircle, textClass: 'text-brand-700', bgClass: 'bg-brand-100', spin: false };
      case 'in_progress':
        return { label: 'In progress', icon: faSpinner, textClass: 'text-brand-700', bgClass: 'bg-brand-100', spin: true };
      case 'failed':
        return { label: 'Failed', icon: faExclamationTriangle, textClass: 'text-red-700', bgClass: 'bg-red-100', spin: false };
      case 'skipped':
        return { label: 'Skipped', icon: faMinus, textClass: 'text-gray-600', bgClass: 'bg-gray-100', spin: false };
      case 'pending':
      default:
        return { label: 'Pending', icon: faClock, textClass: 'text-gray-600', bgClass: 'bg-gray-100', spin: false };
    }
  };

  const renderPostProcessingStatus = () => {
    if (!crawlData) return null;

    const {
      branding_status,
      theme_variants_status,
      theme_variants_count,
      branding_details,
      branding_errors
    } = crawlData;

    const rows: Array<{
      key: 'branding' | 'theme';
      icon: IconDefinition;
      label: string;
      status: NonNullable<FirecrawlStatus['branding_status']>;
    }> = [];

    if (branding_status) {
      rows.push({
        key: 'branding',
        icon: faPalette,
        label: 'Branding extraction',
        status: branding_status
      });
    }

    if (theme_variants_status) {
      rows.push({
        key: 'theme',
        icon: faWandMagicSparkles,
        label: 'Theme variants',
        status: theme_variants_status
      });
    }

    if (!rows.length) {
      return null;
    }

    const normalizedErrors = Array.isArray(branding_errors)
      ? branding_errors
      : branding_errors
        ? [branding_errors]
        : [];

    return (
      <div className="mt-4 border-t border-gray-200 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Post-processing
        </p>
        <div className="mt-3 space-y-3">
          {rows.map(row => {
            const meta = getPostProcessMeta(row.status);
            return (
              <div
                key={row.key}
                className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
              >
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={row.icon} className="mt-1 text-brand-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{row.label}</p>

                    {row.key === 'branding' && row.status === 'completed' && branding_details && (
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-600">
                        {branding_details?.primary_color && (
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block h-3 w-3 rounded-full border border-gray-300"
                              style={{ backgroundColor: branding_details.primary_color }}
                            />
                            <span>Primary {branding_details.primary_color}</span>
                          </div>
                        )}
                        {branding_details?.secondary_color && (
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block h-3 w-3 rounded-full border border-gray-300"
                              style={{ backgroundColor: branding_details.secondary_color }}
                            />
                            <span>Secondary {branding_details.secondary_color}</span>
                          </div>
                        )}
                        {branding_details?.primary_font && (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-5 items-center rounded border border-gray-200 bg-white px-2 font-semibold text-gray-700">
                              {branding_details.primary_font}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {row.key === 'theme' && row.status === 'completed' && typeof theme_variants_count === 'number' && (
                      <p className="text-xs text-gray-500">
                        Generated {theme_variants_count} theme {theme_variants_count === 1 ? 'variant' : 'variants'}.
                      </p>
                    )}

                    {row.status === 'in_progress' && (
                      <p className="text-xs text-gray-500">
                        {row.key === 'branding'
                          ? 'Analyzing your site colors and typography...'
                          : 'Crafting assistant themes to match your brand.'}
                      </p>
                    )}

                    {row.status === 'skipped' && (
                      <p className="text-xs text-gray-500">
                        {row.key === 'branding'
                          ? 'Branding extraction was skipped for this crawl.'
                          : 'Theme variants were not generated for this crawl.'}
                      </p>
                    )}

                    {row.status === 'failed' && (
                      <p className="text-xs text-red-600">
                        {row.key === 'branding'
                          ? 'We could not extract branding automatically. You can retry from the Knowledge Base.'
                          : 'Theme variants could not be generated automatically.'}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold ${meta.bgClass} ${meta.textClass}`}
                  >
                    <FontAwesomeIcon icon={meta.icon} spin={meta.spin} />
                    {meta.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {normalizedErrors.length > 0 && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2">
            <p className="text-xs font-semibold text-red-700">Branding notes</p>
            <ul className="mt-1 space-y-1">
              {normalizedErrors.map((err, idx) => (
                <li key={idx} className="text-xs text-red-600">
                  {err}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Removed: handleAbortCrawl and handleStopCrawl (buttons removed per requirements)

  const renderStatusCard = () => {
    if (!crawlData) return null;

    // Only show Embeddings stage when ALL chunks are completed
    // Check if chunks are all done before determining stage
    const chunksCompleted = crawlData.chunks_completed ?? 0;
    const chunksTotal = crawlData.chunks_total ?? 0;
    const allChunksDone = chunksTotal > 0 && chunksCompleted === chunksTotal;
    
    // Override phase to keep scraping/processing if chunks aren't all done
    let displayPhase = crawlData.phase;
    if (!allChunksDone && (displayPhase === 'awaiting_embeddings' || displayPhase === 'generating_embeddings')) {
      // If chunks aren't done but phase is embeddings, show processing_results instead
      displayPhase = 'processing_results';
    }

    const stageInfo = getStageInfo(crawlData.status, displayPhase);
    const gradientClass = STAGE_GRADIENTS[stageInfo.color] ?? STAGE_GRADIENTS.blue;
    const stageAccent = STAGE_ACCENT_COLORS[stageInfo.color] ?? 'text-brand-500';
    const stageDescription = crawlData.message || stageInfo.description || 'Monitoring crawl progress...';
    const numericTotalPages = parseNumericValue(
      crawlData.total_pages ?? crawlData.estimated_total_pages ?? crawlData.remaining_urls_estimate
    );
    const normalizedProgressInput: ProgressStatus = {
      status: crawlData.status,
      phase: crawlData.phase,
      progress: typeof crawlData.progress === 'number' ? crawlData.progress : undefined,
      chunks_completed: crawlData.chunks_completed,
      chunks_total: crawlData.chunks_total,
      embedding_current: crawlData.embedding_current,
      embedding_total: crawlData.embedding_total,
      total_pages: numericTotalPages,
    };
    const fallbackCalculatedProgress = calculateProgress(normalizedProgressInput);
    const rawProgressValue = typeof crawlData.progress === 'number'
      ? crawlData.progress
      : typeof crawlData.progress_percentage === 'number'
        ? crawlData.progress_percentage
        : fallbackCalculatedProgress;
    const progressValue = clampProgressValue(rawProgressValue);
    const progressLabel = crawlData.progress_label ?? `${Math.round(progressValue)}%`;
    const pagesProcessed = parseNumericValue(crawlData.pages_processed ?? crawlData.pages_scraped) ?? 0;
    const pagesScraped = parseNumericValue(crawlData.pages_scraped ?? crawlData.pages_processed) ?? pagesProcessed;
    const pagesRemaining = parseNumericValue(crawlData.pages_remaining ?? crawlData.pages_in_queue) ?? (
      typeof numericTotalPages === 'number' ? Math.max(numericTotalPages - pagesProcessed, 0) : null
    );
    const pagesFailed = parseNumericValue(crawlData.pages_failed) ?? (Array.isArray(crawlData.errors) ? crawlData.errors.length : 0);
    const embeddingCurrentValue = typeof crawlData.embedding_current === 'number' ? crawlData.embedding_current : null;
    const embeddingTotalValue = typeof crawlData.embedding_total === 'number' ? crawlData.embedding_total : null;
    const activeChunkSource = (Array.isArray(crawlData.active_chunks) && crawlData.active_chunks.length
      ? crawlData.active_chunks
      : (chunkProgress.activeChunks ?? [])).filter((value): value is number => typeof value === 'number');

    const stats = [
      { label: 'Pages stored', value: formatPrimaryStat(pagesProcessed, typeof numericTotalPages === 'number' ? numericTotalPages : null) },
      { label: 'Pages scraped', value: formatStatNumber(pagesScraped) },
      { label: 'Remaining', value: formatStatNumber(pagesRemaining), muted: pagesRemaining === 0 },
      { label: 'Failed', value: formatStatNumber(pagesFailed), highlight: !!pagesFailed && Number(pagesFailed) > 0 },
      { label: 'Chunks', value: `${crawlData.chunks_completed ?? 0}/${crawlData.chunks_total ?? 0}` },
      {
        label: 'Embeddings',
        value: humanizeEmbeddingState(crawlData.embedding_status),
        hint: embeddingTotalValue ? `${embeddingCurrentValue ?? 0}/${embeddingTotalValue} docs` : undefined
      }
    ];

    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex-1 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Current stage</p>
                <div className="flex items-center gap-3 text-gray-900">
                  <FontAwesomeIcon icon={stageInfo.icon} className={`${stageAccent} text-xl`} />
                  <span className="text-2xl font-semibold">{stageInfo.label}</span>
                </div>
                <p className="text-sm text-gray-600 mt-2">{stageDescription}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 font-medium">Progress</p>
                <p className="text-4xl font-semibold text-gray-900">{progressLabel}</p>
              </div>
            </div>
            <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full bg-gradient-to-r ${gradientClass}`} style={{ width: `${progressValue}%` }} />
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2 min-w-0">
                <FontAwesomeIcon icon={faGlobe} className="text-gray-400" />
                <span className="truncate" title={crawlData.url}>{crawlData.url}</span>
              </div>
              {crawlData.start_time && (
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faClock} className="text-gray-400" />
                  <span>{new Date(crawlData.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
            </div>
            {activeChunkSource.length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                <span className="uppercase tracking-wide text-gray-500 font-semibold">Active batches</span>
                {activeChunkSource
                  .slice()
                  .sort((a, b) => a - b)
                  .map(batchId => (
                    <span key={`active-batch-${batchId}`} className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-brand-600 border border-brand-100">
                      <FontAwesomeIcon icon={faSpinner} spin className="text-brand-500" />
                      Batch {batchId}
                    </span>
                  ))}
              </div>
            )}
          </div>

          <div className="w-full lg:w-80 grid grid-cols-2 gap-3">
            {stats.map(stat => (
              <div
                key={stat.label}
                className={`rounded-xl border p-3 ${stat.highlight ? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
              >
                <p className="text-xs uppercase tracking-wide text-gray-500">{stat.label}</p>
                <p className="mt-1 text-xl font-semibold">{stat.value}</p>
                {stat.hint && <p className="text-xs text-gray-500 mt-0.5">{stat.hint}</p>}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6">{renderPostProcessingStatus()}</div>
      </div>
    );
  };

  const renderPagesList = () => {
    const visitedList = crawlData?.visited_urls ?? [];
    const visitedTotal = crawlData?.visited_urls_total ?? visitedList.length;
    const isTrimmed = crawlData?.visited_urls_trimmed ?? false;

    if (!visitedTotal) {
      return (
        <div className="text-center text-gray-500 py-8">
          <FontAwesomeIcon icon={faSearch} className="text-3xl mb-2" />
          <p>No pages found yet</p>
        </div>
      );
    }

    const filteredUrls = visitedList.filter(url => 
      url.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="space-y-2">
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredUrls.map((url, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center space-x-3 min-w-0">
                <FontAwesomeIcon icon={faLink} className="text-brand-500" />
                <span
                  className="text-sm truncate max-w-[240px] inline-block"
                  title={url}
                >
                  {url}
                </span>
              </div>
              <a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-brand-500 hover:text-brand-700 text-sm"
              >
                Visit
              </a>
            </div>
          ))}
        </div>

        {isTrimmed && (
          <p className="text-xs text-gray-500">
            Showing the latest {visitedList.length} of {visitedTotal} pages processed.
          </p>
        )}

        {!filteredUrls.length && !!visitedList.length && (
          <p className="text-xs text-gray-500 text-center">
            No pages match your search. Try a different term.
          </p>
        )}
      </div>
    );
  };

  const renderErrorsList = () => {
    if (!crawlData?.errors?.length) {
      return (
        <div className="text-center text-gray-500 py-8">
          <FontAwesomeIcon icon={faCheckCircle} className="text-3xl mb-2 text-brand-500" />
          <p>No errors encountered</p>
        </div>
      );
    }

    return (
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {crawlData.errors.map((error, index) => (
          <div key={index} className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-1" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading && !crawlData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading crawl details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 text-4xl mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-semibold text-gray-900">
                Crawl Details
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Card */}
        {renderStatusCard()}

        {/* Tabs */}
        {/* Removed tabs - content is always visible */}
      </div>

      {/* Modals removed per requirements */}
    </div>
  );
};

export default CrawlDetails;
